import {
  ApostleFilterInput,
  ApostleLite,
  apostleLiteSchema,
  ApostleSortBy
} from '@apeiron/apostle'
import { DEFAULT_PAGE_INFO, SortInput } from '@apeiron/common'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import graphql from '@src/graphql/query/getApostles'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetApostles = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<ApostleLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        filterInput = {},
        pageInput = {},
        sortInput = { sortBy: ApostleSortBy.IDAsc }
      } = request

      const response = await query({
        variables: {
          filterInput: R.reject(R.isNil, filterInput),
          pagingInput: serializePagingInput(pageInput),
          sortInput
        }
      })

      const apostles = deserializeListData<ApostleLite>(
        response,
        apostleLiteSchema
      ) as ApostleLite[]

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(apostles)

      setPageInfo(pageInfo)

      return {
        data: apostles,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    fetch,
    loading,
    pageInfo
  }
}

type Request = {
  filterInput?: ApostleFilterInput
  pageInput?: PageInput
  sortInput?: SortInput<ApostleSortBy>
}

type Response = {
  data: ApostleLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: ApostleLite[]
  fetch: (request: Request) => Promise<Response>
  loading: boolean
  pageInfo: PageInfo
}

export default useGetApostles
